import React, { createContext, useEffect, useState, useContext, useCallback } from 'react';
import { auth } from '../firebase';
import { onAuthStateChanged, signInWithPopup } from 'firebase/auth';
import { googleProvider } from '../firebase';
import { api, resetAuthErrors, setAuthErrorHandler, checkAuthAccess } from '../api';

const AuthCtx = createContext();
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const [forceRender, setForceRender] = useState(0);

  // Function to force a re-render when auth errors change
  const triggerRerender = useCallback(() => {
    setForceRender(prev => prev + 1);
  }, []);

  // Function to logout
  const logout = useCallback(async () => {
    try {
      console.log("Logging out user");
      resetAuthErrors();
      await auth.signOut();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }, []);

  // Handle auth errors (whitelist errors, etc.)
  useEffect(() => {
    console.log("Setting up auth error handler");
    
    // Set up the handler for auth errors
    setAuthErrorHandler((errorType, message) => {
      console.log(`Auth error handler called: ${errorType}`, message);
      
      // Don't automatically sign out for whitelist errors
      // Let the user see the error message and decide to sign out manually
      setAuthChecked(true);
      
      // Force a re-render to ensure error state is reflected in UI
      triggerRerender();
    });
  }, [triggerRerender]);

  useEffect(() => {
    console.log("Setting up auth state change listener");
    
    // (Optional) dev-only guard against HMR double mount
    if (import.meta.hot && window.__AUTH_LISTENER_ATTACHED__) return;
    if (import.meta.hot) window.__AUTH_LISTENER_ATTACHED__ = true;
    
    const unsubscribe = onAuthStateChanged(auth, async (u) => {
      console.log("Auth state changed:", u?.email);
      setUser(u);
      setLoading(false);
      
      // We don't do the auth check here anymore
      // Login component does immediate check after sign-in
      // This avoids race conditions
      if (!u) {
        resetAuthErrors();
        setAuthChecked(true);
      }
    });
    
    return unsubscribe;
  }, []);

  const login = async () => {
    try {
      resetAuthErrors();
      console.log("Starting login process");
      return await signInWithPopup(auth, googleProvider);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  return (
    <AuthCtx.Provider value={{ 
      user, 
      login, 
      logout, 
      loading,
      authChecked,
      forceRender  // Include this so components can depend on it
    }}>
      {children}
    </AuthCtx.Provider>
  );
}